import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";

const blankField = (type = "text") => ({
  id: `fld_${Math.random().toString(36).slice(2, 8)}`,
  type,
  label: "",
  required: false,
  placeholder: "",
  options: type === "text" ? [] : [{ id: `opt_${Math.random().toString(36).slice(2, 8)}`, label: "" }],
});

export default function AdminContactForm() {
  const [tab, setTab] = useState("form");
  const [form, setForm] = useState(null);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadForm = () => {
    api.get("/merchant/contact-form").then(({ data }) => setForm(data)).catch((e) => toast.error(extractErr(e)));
  };
  const loadEntries = () => {
    api.get("/merchant/contact-form/entries").then(({ data }) => setEntries(data.items || [])).catch((e) => toast.error(extractErr(e)));
  };
  useEffect(() => { loadForm(); }, []);
  useEffect(() => { if (tab === "data") loadEntries(); }, [tab]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/contact-form", form);
      setForm(data);
      toast.success("Contact form saved");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/merchant/contact-form/entries/${id}`);
      setEntries((rows) => rows.filter((r) => r.id !== id));
    } catch (e) { toast.error(extractErr(e)); }
  };

  const setField = (id, patch) => setForm({ ...form, fields: form.fields.map((f) => f.id === id ? { ...f, ...patch } : f) });

  return (
    <div className="max-w-3xl space-y-4" data-testid="admin-contact-form">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Form At Contact Page</h1>
        <p className="text-sm text-slate-500 mt-1">One form appears on /page/contact, directly under the page title.</p>
      </div>
      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-fit">
        <button type="button" data-testid="contact-form-tab-form" onClick={() => setTab("form")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "form" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Form</button>
        <button type="button" data-testid="contact-form-tab-data" onClick={() => setTab("data")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "data" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Data</button>
      </div>

      {tab === "form" && form && (
        <Card className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Form title</Label>
              <Input data-testid="contact-form-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Button label</Label>
              <Input data-testid="contact-form-button" value={form.button_label || ""} onChange={(e) => setForm({ ...form, button_label: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Thank-you message</Label>
            <Input value={form.success_message || ""} onChange={(e) => setForm({ ...form, success_message: e.target.value })} />
          </div>
          <div className="space-y-3">
            {(form.fields || []).map((field, index) => (
              <div key={field.id} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`contact-field-${index}`}>
                <div className="grid sm:grid-cols-[1fr_160px_auto] gap-2">
                  <Input placeholder="Field label" value={field.label} onChange={(e) => setField(field.id, { label: e.target.value })} />
                  <select className="h-10 rounded-md border border-slate-200 px-2 text-sm" value={field.type} onChange={(e) => setField(field.id, { type: e.target.value, options: e.target.value === "text" ? [] : (field.options?.length ? field.options : [{ id: `opt_${index}`, label: "" }]) })}>
                    <option value="text">Text</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="multiselect">Multi-select ticks</option>
                  </select>
                  <Button type="button" variant="ghost" onClick={() => setForm({ ...form, fields: form.fields.filter((f) => f.id !== field.id) })}><Trash2 className="w-4 h-4" /></Button>
                </div>
                {field.type === "text" ? (
                  <Input placeholder="Placeholder" value={field.placeholder || ""} onChange={(e) => setField(field.id, { placeholder: e.target.value })} />
                ) : (
                  <div className="space-y-2">
                    {(field.options || []).map((opt, oi) => (
                      <div key={opt.id || oi} className="flex gap-2">
                        <Input placeholder="Option" value={opt.label} onChange={(e) => setField(field.id, { options: field.options.map((o, k) => k === oi ? { ...o, label: e.target.value } : o) })} />
                        <Button type="button" variant="ghost" onClick={() => setField(field.id, { options: field.options.filter((_, k) => k !== oi) })}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => setField(field.id, { options: [...(field.options || []), { id: `opt_${Date.now()}`, label: "" }] })}>Add option</Button>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={Boolean(field.required)} onCheckedChange={(v) => setField(field.id, { required: v })} />
                  Required
                </label>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" data-testid="contact-add-field" onClick={() => setForm({ ...form, fields: [...(form.fields || []), blankField("text")] })}><Plus className="w-4 h-4 mr-1" /> Add field</Button>
            <Button type="button" data-testid="contact-form-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save form"}</Button>
          </div>
        </Card>
      )}

      {tab === "data" && (
        <div className="space-y-3" data-testid="contact-form-data">
          {entries.length === 0 ? <Card className="p-6 text-sm text-slate-500">No submissions yet.</Card> : null}
          {entries.map((row) => (
            <Card key={row.id} className="p-4" data-testid={`contact-entry-${row.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</div>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeEntry(row.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <dl className="mt-2 space-y-1 text-sm">
                {(row.answers || []).map((a) => (
                  <div key={a.id} className="grid sm:grid-cols-[160px_1fr] gap-1">
                    <dt className="text-slate-500">{a.label}</dt>
                    <dd className="text-slate-900 break-words">{Array.isArray(a.value) ? a.value.join(", ") : (a.value || "—")}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
